import { Theme, makeStyles, createStyles, Hidden } from '@material-ui/core';
import React, { FC, useContext, useEffect, useState } from 'react';
import { FormContext, FormControlProps } from '..';
import { TableDefinition, TableRowDefinition } from '../services';
import { registry } from '../registry';
import { LayoutNodeWrapper } from './LayoutNode';
import { VISIBILITY_PATH_STARTING_INDEX } from '../constants';

export interface TableRowProps extends FormControlProps<TableRowDefinition> {
  rowIndex?: number;
  header?: TableRowDefinition;
  tableDefinition?: TableDefinition;
}

const useStyles = makeStyles(function(theme: Theme) {
  return createStyles({
    root(props: TableRowProps) {
      return {
        flex: 1,
        marginBottom: theme.spacing(1),
        display: 'flex',
        backgroundColor: props.definition.style?.background ?? 'inherit'
      }
    },
    bodyRow(props: TableRowProps) {
      return {
        backgroundColor: props.definition.style?.background ?? 'inherit'
      }
    },
    rowHeader: {},
    headerCell(props: TableRowProps) {
      const styles: Record<string, string> = {
        padding: props.definition.style?.padding ?? '6px',
        verticalAlign: 'middle'
      };
      if (props.tableDefinition?.style?.bordered) {
        styles.border = '1px solid #bdbdbd8c'
      }
      return styles;
    },
    bodyCell(props: TableRowProps) {
      const styles: Record<string, any> = {
        padding: props.definition.style?.padding ?? '6px',
        verticalAlign: 'top'
      };

      if (props.tableDefinition?.style?.bordered) {
        styles.border = '1px solid #bdbdbd8c'
      }

      if (props.tableDefinition?.style?.condensedBehavior === 'stack') {
        if (props.tableDefinition?.style?.headerColumnWidth) {
          styles[theme.breakpoints.up('md')] = {
            '&:first-child': {
              width: props.tableDefinition?.style.headerColumnWidth
            }
          }
        }
      }
      return styles;
    },
    bordered: {
      // '&:not(:last-child)': {
        borderRight: '1px solid #bdbdbd8c !important',
      // },
      borderBottom: '1px solid #bdbdbd8c !important',
      borderTop: '1px solid #bdbdbd8c !important',
    }
  })
});

export const TableRow: FC<TableRowProps> = function(props) {
  const { definition, path } = props;
  const classes = useStyles(props);
  const { visibilityMgr } = useContext(FormContext);
  const [isVisible, setIsVisible] = useState(true);
  const styles = definition?.style ? definition?.style : {};

  useEffect(() => {
    const subscription = visibilityMgr!.watch(path.slice(VISIBILITY_PATH_STARTING_INDEX))
      .subscribe((visibility: any) => {
        setIsVisible(visibility);
      });

      return subscription.unsubscribe();
  }, [visibilityMgr, path])

  useEffect(() => {
    if (!definition.children) {
      setIsVisible(false);
    }
  }, [definition]);

  return (
    isVisible
    ? (
      <React.Fragment>
        <Hidden mdUp>
          <tr className = { classes.bodyRow }>
            {
              <td className = { classes.bodyCell } key = { 0 } colSpan={props.header?.children.length}>
                <LayoutNodeWrapper
                  path = { `${path.concat('/children/')}0/content` } tableCell={true}>
                </LayoutNodeWrapper>
              </td>
            }
          </tr>
          <tr className = { classes.bodyRow }>
            {
              definition.children.slice(1).map((cell, cellIndex) => {
                let colSpan = 1;
                if ((cell as any).colSpan) {
                  colSpan = (cell as any).colSpan;
                } else if (cell.style?.fullWidth) {
                  colSpan = definition.children.length;
                }

                return <td className = { cellIndex === 0 ? classes.headerCell : classes.bodyCell } key = { cellIndex + 1 } colSpan={colSpan} data-th={props.header?.children?.[cellIndex + 1]?.name}>
                  <LayoutNodeWrapper
                    path = { `${path.concat('/children/')}${+cellIndex + 1}/content` } tableCell={true}>
                  </LayoutNodeWrapper>
                </td>
              })
            }
          </tr>
        </Hidden>
        <Hidden smDown>
          <tr className = { classes.bodyRow }>
            {
              definition.children.map((cell, cellIndex) => {
                let colSpan = 1;
                if ((cell as any).colSpan) {
                  colSpan = (cell as any).colSpan;
                } else if (cell.style?.fullWidth) {
                  colSpan = 10;
                }
                let cellClass = '';
                if (cellIndex < definition.children.length && !cell.style?.fullWidth) {
                  cellClass = classes.bordered;
                }

                return <td className = { `${classes.bodyCell} ${cellClass}` } key = { cellIndex } colSpan={colSpan} data-th={props.header?.children?.[cellIndex]?.name}>
                  <LayoutNodeWrapper
                    path = { path.concat('/children/') + cellIndex + '/content' } tableCell={true}>
                  </LayoutNodeWrapper>
                </td>
              })
            }
          </tr>
        </Hidden>
      </React.Fragment>
    ) : null
  )
};

registry.registerComponent('TableRow', TableRow);
